import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categoryMap, categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? categoryMap[slug] : undefined;

  if (!category) {
    return (
      <>
        <SEOHead
          title="分类未找到 - 蘑菇TV入口"
          description="抱歉，您查找的分类不存在。请返回首页浏览其他分类。"
          keywords={siteInfo.keywords}
          canonical={`${siteInfo.url}/category/${slug}`}
        />
        <Navbar />
        <main className="pt-24 pb-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-error-warning-line text-3xl text-primary-500"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 mb-3 font-heading">分类未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的分类不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = getArticlesByCategory(slug || '');
  const pageTitle = `${category.name} - ${siteInfo.name}`;
  const pageDescription = category.description;
  const pageUrl = `${siteInfo.url}/category/${slug}`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`${category.name},${siteInfo.keywords}`}
        canonical={pageUrl}
        ogTitle={pageTitle}
        ogDescription={pageDescription}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-14 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mb-10">
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100">
                <i className={`${category.icon} text-xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.articleCount} 篇文章</p>
              </div>
            </div>
            <p className="text-foreground-600 text-sm leading-relaxed max-w-3xl">{category.description}</p>
          </div>

          {/* Category Switcher */}
          <div className="flex flex-wrap gap-2 mb-8 pb-6 border-b border-background-200/70">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-file-list-3-line text-2xl text-secondary-500"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}